import { ArrowRight, Truck, ShieldCheck, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/lib/site";

export default function NordItalia() {
  return (
    <section
      id="nord-italia"
      data-testid="nord-italia-section"
      className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-brand-surface overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* LEFT — pitch */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 reveal">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold text-brand-primary">
              <span className="w-8 h-px bg-brand-primary" />
              Lunga percorrenza
            </div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-[64px] leading-[0.95] tracking-tighter text-brand-ink mt-5 text-balance">
              Dalla Sicilia.<br />
              <span className="text-brand-primary">A tutto</span><br />
              il Nord Italia.
            </h2>
            <p className="mt-6 text-lg text-brand-body leading-relaxed max-w-md">
              Milano, Torino, Bologna, Verona, Roma. Tratte settimanali con mezzi dedicati, tracciamento GPS e
              <strong className="text-brand-ink"> assicurazione completa porta-a-porta</strong>.
              I vostri ricordi viaggiano in prima classe.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              <Pill icon={<Truck className="w-4 h-4" />} label="Mezzi dedicati" />
              <Pill icon={<MapPin className="w-4 h-4" />} label="Tracciamento GPS" />
              <Pill icon={<ShieldCheck className="w-4 h-4" />} label="Assicurazione" />
            </div>

            <Button
              asChild
              className="mt-8 w-full sm:w-auto h-14 px-7 rounded-full bg-brand-primary hover:bg-brand-ink text-white text-base font-bold shadow-xl shadow-brand-primary/20"
              data-testid="nord-italia-cta"
            >
              <a href="#preventivo" className="justify-center">
                Preventivo Nord Italia
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>

          {/* RIGHT — routes visual */}
          <div className="lg:col-span-7 space-y-5">
            {/* Branded truck showcase */}
            <div className="relative rounded-3xl overflow-hidden border border-brand-line shadow-brand reveal">
              <img
                src="https://customer-assets.emergentagent.com/wingman/19948741-6e04-4179-9f17-40c9ab60aafe/attachments/9a64e01f8bbf4d26a227a0130d84181b_5b0133a2-4391-4deb-9e31-cca39f2b26c7.jpg"
                alt="Furgone Sicilia Traslochi in piazza, pronto per partire verso il Nord Italia"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/95 text-brand-primary text-[11px] uppercase tracking-[0.18em] font-bold rounded-full">
                I nostri mezzi · Sicilia Traslochi
              </div>
            </div>

            <div className="relative bg-white border border-brand-line rounded-3xl p-6 lg:p-10 shadow-brand reveal reveal-delay-1">
              <div className="absolute -top-3 left-7 px-3 py-1 bg-brand-ink text-white text-[10px] tracking-[0.2em] uppercase font-bold rounded">
                Tratte attive · 2025
              </div>

              <div className="mt-3 space-y-2">
                {ROUTES.map((r, i) => (
                  <div
                    key={`${r.from}-${r.to}`}
                    data-testid={`route-${i}`}
                    className="group relative grid grid-cols-12 gap-2 sm:gap-4 items-center py-3 sm:py-4 px-2 sm:px-3 rounded-xl hover:bg-brand-surface transition-colors border-b border-brand-line last:border-b-0"
                  >
                    <div className="col-span-5 sm:col-span-4">
                      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-brand-body font-bold">Partenza</div>
                      <div className="font-display font-bold text-base sm:text-lg lg:text-xl text-brand-ink mt-0.5 truncate">{r.from}</div>
                    </div>

                    <div className="col-span-2 sm:col-span-3 flex items-center justify-center">
                      <div className="relative w-full flex items-center">
                        <div className="flex-1 h-px bg-gradient-to-r from-brand-primary/40 via-brand-primary to-brand-primary/40" />
                        <Truck className="absolute left-1/2 -translate-x-1/2 w-4 h-4 sm:w-5 sm:h-5 text-brand-primary bg-white px-0.5 transition-transform group-hover:translate-x-2" />
                      </div>
                    </div>

                    <div className="col-span-5 sm:col-span-3 text-right sm:text-left">
                      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-brand-body font-bold">Destinazione</div>
                      <div className="font-display font-bold text-base sm:text-lg lg:text-xl text-brand-primary mt-0.5 truncate">{r.to}</div>
                    </div>

                    <div className="hidden sm:block sm:col-span-2 text-right">
                      <div className="text-xs font-semibold text-brand-ink">{r.km}</div>
                      <div className="text-[11px] text-brand-body">{r.days}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-brand-line flex items-center justify-between flex-wrap gap-3">
                <p className="text-sm text-brand-body">
                  Non vedete la vostra tratta? <strong className="text-brand-ink">Operiamo ovunque in Italia.</strong>
                </p>
                <a
                  href="#preventivo"
                  className="text-sm font-bold text-brand-primary link-underline"
                  data-testid="custom-route-link"
                >
                  Chiedi una tratta personalizzata
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pill({ icon, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-brand-line bg-white">
      <span className="text-brand-primary">{icon}</span>
      <span className="text-[11px] uppercase tracking-wider font-bold text-brand-ink text-center leading-tight">{label}</span>
    </div>
  );
}
